import {
  AmountType,
  CalculateByField,
  ChartConfiguration,
  ConditionDescriptor,
  ConditionEqualityType,
  DimensionDescriptor,
  DrillDownColumn,
  FailedMeasure,
  FieldConfiguration,
  GroupTotal,
  MeasureDescriptor,
  MeasureUnit,
  MeasureUnitTable,
  MetricDescriptor,
  PivotConfiguration,
  RunTabularReportRequest,
  TabularConfiguration,
  TileConfiguration,
} from "../../shared/reporting/api/biClient.types";

import {
  AccountingBook,
  DimensionsStructure,
  RunPivotReportRequest,
} from "../../shared/reporting/types/biShared.types.ts";
import { Maybe } from "../../shared/types";

export type MeasureDataset = {
  name: string;
  table: MeasureUnitTable;
  values: { [key: string]: string[] };
};

export type MeasuresStructure = {
  names: string[];
  groups: MeasureGroup[];
};

export type MeasureGroup = {
  caption: string;
  names: string[];
};

export type MetricsStructure = {
  names: string[];
  groups: MeasureGroup[];
};

export type MetaDataResponse = {
  dataSets: DataSetMetaData[];
};

export type DataSetMetaData = {
  dataSetId: string;
  dimensionsStructure: DimensionsStructure;
  dimensions: DimensionDescriptor[];
  measuresStructure: MeasuresStructure;
  measures: MeasureDescriptor[];
  functions: FunctionDescription[];
  measureGroups: string[];
  metrics: MetricDescriptor[];
  metricsStructure: MetricsStructure;
};

export type FunctionDescription = {
  name: string;
  fullName: string;
  description: string;
  args: FunctionArgumentDescription[];
};

type FunctionArgumentDescription = {
  name: string;
  description: string;
};

export type BuildReportRequest = BuildPivotReportRequest | BuildTabularReportRequest | BuildChartReportRequest;

export type BuildPivotReportRequest = Omit<PivotConfiguration, "reportNature"> & {
  reportId: string;
  sessionId: string;
  cacheSessionId?: string;
  withDrilldown: boolean;
  useQueryLogging?: boolean;
};

export type BuildApiReportRequest = BuildTabularReportRequest;

export type BuildTabularReportRequest = TabularConfiguration & {
  reportId: string;
  sessionId: string;
  cacheSessionId?: string;
  withDrilldown: boolean;
  skip: number;
  take: number;
  calcTotalCount?: boolean;
  calcGroupTotals?: boolean;
  useQueryLogging?: boolean;
};

export type BuildTileReportRequest = TileConfiguration & {
  reportId: string;
  sessionId: string;
  cacheSessionId?: string;
  skip: number;
  take: number;
  useQueryLogging?: boolean;
};

export type ExportParameters = {
  name: string;
  equality?: ConditionEqualityType;
  values: string[];
};

export type ExportConfig = {
  withParameters: boolean;
  parameters: ExportParameters[];
};

export type BuildExportRequest = {
  exportConfig: ExportConfig;
  buildConfig: BuildTabularReportRequest | BuildPivotReportRequest;
};

export type RunExportRequest = {
  exportConfig: ExportConfig;
  viewConfig: RunTabularReportRequest | RunPivotReportRequest;
};

export type BuildChartReportRequest = Omit<ChartConfiguration, "reportNature"> & {
  reportId: string;
  sessionId: string;
  cacheSessionId?: string;
  withDrilldown: boolean;
  useQueryLogging?: boolean;
};

export type DrillDownDataRequest = {
  skip: number;
  take: number;
  calcTotalCount?: boolean;
  table: MeasureUnitTable;
  allocated: boolean;
  columns: DrillDownColumn[];
  conditions: ConditionDescriptor[];
  sortingColumns: ColumnSorting[];
};

export type CashFlowExportRequest = {
  caption: string;
  drillDownInfo: IrrDrillDownInfo;
};

export type ChartOfAccountsExportRequest = {
  caption: string;
  sessionId: string;
  itemId: string;
  table: MeasureUnitTable;
  includedOnly: boolean;
  accountingBook: Maybe<string>;
};

export type DrillDownRow = Record<string, string>;

export type DrillDownDataResponse = {
  data: DrillDownRow[];
  totalCount?: number;
};

export type TabularGroupTotalsResponse = {
  groupTotals: GroupTotal[];
  failedMeasures: FailedMeasure[];
};

export type ColumnSorting = DrillDownColumn & {
  sortAsc: boolean;
  caption: string | undefined;
};

export type ExportResponse = {
  token: string;
  fileProcessed: boolean;
  error?: boolean;
  errorText?: string;
};

export type CustomMeasureUnit = {
  variable: string;
  unit: MeasureUnit;
};

export type CustomMeasureDescription = {
  id?: string;
  name: string;
  caption: string;
  group: string;
  formula: string;
  calculateBy: CalculateByField;
  units: CustomMeasureUnit[];
  configuration: FieldConfiguration;
  amountType: AmountType;
};

export type LedgerBasisRequest = {
  dimensionNames: string[];
  measureNames: string[];
};

export type LedgerBasisResponse = {
  transactional: boolean;
};

export type ChartOfAccounts = {
  gl: ChartOfAccountsItem[];
  memo: ChartOfAccountsItem[];
};

export type ChartOfAccountsItem = {
  accountNo: string;
  accountName: string;
  accountCategory: string;
  accountType: AccountType;
  accountSubcategory: string;
  incomeBalance: string;
  level: number;
  accountingBook: string | null;
};

export enum AccountType {
  Heading = "Heading",
  BeginTotal = "Begin-Total",
  EndTotal = "End-Total",
  Posting = "Posting",
  Total = "Total",
}
export enum CellDrillDownInfoType {
  Default = "Default",
  Irr = "Irr",
  Metric = "Metric",
}

export interface ClientSettings {
  intercomAuthentication?: IntercomAuthSettings;
  accountingBookSettings?: AccountingBookSettings;
  dataSets?: DataSetInfo[];
}

interface IntercomAuthSettings {
  intercomUserId: string;
  intercomUserHash: string;
  intercomUserSignedUp: string;
  intercomAppId: string;
}

export type AccountingBookSettings = {
  multiBook: boolean;
  books: AccountingBook[];
};

export type DataSetInfo = {
  id: string;
  name: string;
};

export type DrillDownConfiguration = {
  primaryDimension: string;
  defaultDimensions: string[];
};

export type DrillDownInfoResult = {
  item?: DrillDownInfo;
  notFound: boolean;
};

export type DrillDownInfo = {
  combined: CellDrillDownInfoTypes;
  items: CellDrillDownInfoTypes[];
};

export type CellDrillDownInfoTypes = CellDrillDownInfo | IrrDrillDownInfo | MetricDrillDownInfo;

export type CellDrillDownInfoBase = {
  conditions: ConditionDescriptor[];
  drillDownInfoType: CellDrillDownInfoType;
};

export type MeasureDrillDownInfo = CellDrillDownInfoBase & {
  drillDownInfoType: CellDrillDownInfoType.Default | CellDrillDownInfoType.Irr;
  table: MeasureUnitTable;
  field: CalculateByField;
  allocated: boolean;
  amountType: AmountType;
};

export type MetricDrillDownInfo = CellDrillDownInfoBase & {
  drillDownInfoType: CellDrillDownInfoType.Metric;
  metricDimension: string;
};

export type CellDrillDownInfo = MeasureDrillDownInfo & {
  drillDownInfoType: CellDrillDownInfoType.Default;
};

export type IrrDrillDownInfo = MeasureDrillDownInfo & {
  drillDownInfoType: CellDrillDownInfoType.Irr;
  cashFlowValues?: CashFlowValue[];
  referenceDateFieldCaption?: string;
  amountFieldCaption?: string;
};

export type CashFlowValue = {
  date: string;
  amount: number;
  isNav: boolean;
  isBeginningOfPeriod?: boolean;
  description: string;
  dateFormatted: string;
  amountFormatted: string;
  drillDownInfo?: DrillDownInfo;
};

export type DrillDownCacheValidationResponse = {
  isCacheValid: boolean;
  expired: boolean;
};

export type ChartOfAccountsDataResponse = {
  items: ChartOfAccountsLedgerItem[];
};

export type ChartOfAccountsLedgerItem = {
  measureUnitTable: MeasureUnitTable;
  items: ChartOfAccountsDataItem[];
};

export type ChartOfAccountsDataItem = ChartOfAccountsItem & {
  amount: number | undefined;
  amountFormatted: string | undefined;
  conditions: ConditionDescriptor[];
  included: boolean;
};

export type UsedByReportSource = "Template" | "API Analytics" | "Report";

export interface UsedByReport {
  id: string;
  reportName: string;
  source: UsedByReportSource;
  clientCode: string;
  organization: string;
}

export interface UsedByReportsResponse {
  reports: UsedByReport[];
}
